import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useCountUp } from '../lib/useCountUp';
import { Card } from '../components/Card';
import { Chip, type ChipTone } from '../components/Chip';
import { FastingWindowCard } from '../components/FastingWindowCard';
import { MacroBar } from '../components/MacroBar';
import { MacroRing } from '../components/MacroRing';
import { Nav } from '../components/Nav';
import { LoadingState } from '../components/LoadingState';
import { TrajectoryChart, type TrajectoryActual } from '../components/TrajectoryChart';
import { WeeklyMacroChart } from '../components/WeeklyMacroChart';
import { WeightSparkline, type SparklinePoint } from '../components/WeightSparkline';
import {
  api,
  type Bet,
  type DailyLog,
  type DailyRangeEntry,
  type DashboardPayload,
  type WeeklyMilestone,
} from '../lib/api';

export default function Dashboard() {
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [todayLog, setTodayLog] = useState<DailyLog | null>(null);
  const [range, setRange] = useState<DailyRangeEntry[] | null>(null);
  const [bet, setBet] = useState<Bet | null>(null);
  const [milestones, setMilestones] = useState<WeeklyMilestone[] | null>(null);
  const [betRange, setBetRange] = useState<DailyRangeEntry[] | null>(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [d, t] = await Promise.all([
        api.getDashboard(),
        api.getToday(),
      ]);
      setData(d);
      setTodayLog(t);
      // 14-day window for the weight sparkline + 7-day for the weekly macro
      // expansion. We fetch 14 to cover both — the chart renders the last 7.
      const todayISO = t.log_date;
      const start = isoNDaysBefore(todayISO, 13);
      try {
        setRange(await api.getDailyRange(start, todayISO));
      } catch {
        setRange(null);
      }
      // Bet + full-bet weight history + milestone glide path → trajectory chart.
      // Independent of the 14-day fetch so the sparkline keeps loading even if
      // the trajectory data is still in flight or the bet endpoint 404s.
      try {
        const b = await api.getBet();
        setBet(b);
        const [ms, betWide] = await Promise.all([
          api.getMilestones().catch(() => null),
          api.getDailyRange(b.starting_date, todayISO).catch(() => null),
        ]);
        setMilestones(ms?.milestones ?? null);
        setBetRange(betWide);
      } catch {
        setBet(null);
        setMilestones(null);
        setBetRange(null);
      }
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Hero countdown counts up once data lands (target flips 0 → real value).
  const daysCount = useCountUp(data?.today.days_to_deadline ?? 0);

  if (!data) {
    return (
      <>
        <div className="min-h-full flex items-center justify-center text-fg-muted pb-24">
          {error ? (
            <div className="text-center space-y-3 px-6">
              <pre className="text-xs text-danger whitespace-pre-wrap">{error}</pre>
              <Link to="/today" className="btn-primary inline-block px-5 py-3 text-sm">
                Go to Today
              </Link>
            </div>
          ) : (
            <LoadingState />
          )}
        </div>
        <Nav />
      </>
    );
  }

  const { today, pace, today_checklist: checklist, creatine_masking } = data;
  const masking = creatine_masking?.active ?? false;
  const rise = (i: number): CSSProperties => ({ '--i': i }) as CSSProperties;

  return (
    <>
      <div className="min-h-full max-w-md lg:max-w-5xl mx-auto px-5 lg:px-8 pt-8 lg:pt-12 pb-32">
        <header className="animate-rise mb-5 flex items-baseline justify-between gap-3">
          <p className="label-eyebrow">
            GIOS · {today.day_of_week.slice(0, 3).toUpperCase()}
            {today.week_number > 0 ? ` · W${today.week_number}` : ''} · BLOCK {today.block}
          </p>
          <p className="hidden font-display text-2xl leading-none text-fg-muted lg:block">
            The Protocol
          </p>
        </header>

        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-12 lg:gap-5 lg:items-start">
          {/* Hero — the countdown carries the page. `tone="hero"` re-declares the
              semantic tokens for this subtree, so everything below inverts to
              paper-on-ink on its own. Never hardcode a colour in here. */}
          <Card tone="hero" className="animate-rise lg:col-span-5" style={rise(0)}>
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="font-display text-7xl leading-[0.85] lg:text-8xl tabular-nums">
                  {daysCount}
                </p>
                <p className="label-eyebrow mt-2">days to deadline</p>
              </div>
              <div className="flex flex-col items-end gap-1.5 pb-1">
                {masking ? (
                  // Masking means "pace has no valid data yet", which the system
                  // maps to the quiet grey chip — not `accent`, whose solid slab
                  // would out-shout the countdown it sits beside. The paragraph
                  // below carries the explanation.
                  <Chip tone="muted" dot>
                    Creatine water · {creatine_masking?.day_of_load}/{creatine_masking?.masking_days}
                  </Chip>
                ) : (
                  <Chip tone={paceTone(pace.status)} dot>
                    {paceText(pace.status)}
                    {pace.estimated_probability_pct !== null &&
                      ` · ${pace.estimated_probability_pct}%`}
                  </Chip>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-1 pt-4 mt-4 border-t border-line">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                  Weight
                </span>
                <span className="font-mono text-sm tabular-nums text-fg">
                  {today.current_weight_kg !== null ? `${today.current_weight_kg.toFixed(1)} kg` : '— kg'}
                  <span className="text-fg-faint mx-1.5">→</span>
                  {today.target_weight_today_kg !== null ? `${today.target_weight_today_kg} kg` : '—'}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                  Δ to weekly target
                </span>
                <span
                  className={`font-mono text-sm tabular-nums ${
                    today.weight_delta_kg === null
                      ? 'text-fg-muted'
                      : today.weight_delta_kg <= 0
                      ? 'text-good'
                      : 'text-warn'
                  }`}
                >
                  {fmtDelta(today.weight_delta_kg)}
                </span>
              </div>
            </div>
            {masking && creatine_masking && (
              <p className="text-xs text-fg-muted leading-relaxed pt-3 mt-3 border-t border-line">
                Scale data masked by intramuscular water gain. Pace ignored until{' '}
                {addDaysISO(creatine_masking.load_started_at, creatine_masking.masking_days)}. Track
                waist + photos.
              </p>
            )}
            {range && range.length > 1 && (
              <div className="pt-3 mt-3 border-t border-line">
                <WeightSparkline
                  points={rangeToSparklinePoints(range)}
                  goal={today.target_weight_today_kg ?? undefined}
                />
              </div>
            )}
          </Card>

          {milestones && milestones.length > 1 && bet && (
            <Card
              eyebrow="Trajectory"
              hint={`${bet.starting_weight_kg.toFixed(1)} kg → ${bet.target_weight_kg.toFixed(1)} kg over ${milestones.length} weeks`}
              className="animate-rise lg:col-span-7"
              style={rise(1)}
            >
              <TrajectoryChart
                milestones={milestones}
                actuals={binActualsByWeek(betRange ?? [], bet.starting_date)}
                currentWeek={today.week_number > 0 ? today.week_number : undefined}
                currentWeightKg={today.current_weight_kg}
              />
            </Card>
          )}

          <GoalProbabilityCard
            pct={pace.estimated_probability_pct}
            status={pace.status}
            trailingLossKg={pace.trailing_4wk_avg_loss_kg}
            kgToTarget={today.kg_to_target}
            daysToDeadline={today.days_to_deadline}
            className="animate-rise lg:col-span-4"
            style={rise(2)}
          />

          <div className="animate-rise lg:col-span-4" style={rise(3)}>
            <FastingWindowCard
              start={data.eating_window.start}
              end={data.eating_window.end}
              onSave={async (start, end) => {
                const updated = await api.patchUserSettings({
                  eating_window_start: start,
                  eating_window_end: end,
                });
                setData((d) =>
                  d
                    ? {
                        ...d,
                        eating_window: {
                          start: updated.eating_window_start,
                          end: updated.eating_window_end,
                        },
                      }
                    : d,
                );
              }}
            />
          </div>

          {todayLog && (
            <Card eyebrow="Nutrition · today" className="animate-rise lg:col-span-4" style={rise(4)}>
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-stretch">
                <div className="shrink-0">
                  <MacroRing
                    consumed={todayLog.macros.kcal.consumed}
                    target={todayLog.macros.kcal.target}
                    unit="kcal"
                  />
                </div>
                <div className="flex-1 flex flex-col justify-center gap-3 w-full">
                  <MacroBar
                    label="Protein"
                    consumed={todayLog.macros.protein.consumed}
                    target={todayLog.macros.protein.target}
                  />
                  <MacroBar
                    label="Fat"
                    consumed={todayLog.macros.fat.consumed}
                    target={todayLog.macros.fat.target}
                  />
                  <MacroBar
                    label="Carbs"
                    consumed={todayLog.macros.carbs.consumed}
                    target={todayLog.macros.carbs.target}
                  />
                </div>
              </div>
            </Card>
          )}

          <Card eyebrow="Today" className="animate-rise lg:col-span-7" style={rise(5)}>
            <div className="grid grid-cols-3 gap-1.5">
              {checklist.meals.map((m) => (
                <CheckPill key={m.id} label={shortMealLabel(m.label, m.id)} done={m.completed} />
              ))}
              <CheckPill label="Training" done={checklist.training.completed} />
              <CheckPill
                label={`Steps ${formatStepsShort(checklist.steps.current, checklist.steps.target)}`}
                done={checklist.steps.current >= checklist.steps.target}
              />
              <CheckPill label="Mobility" done={checklist.mobility_completed} />
              <CheckPill label="Creatine" done={checklist.creatine_taken} />
              <CheckPill label="Magnesium" done={checklist.magnesium_taken} />
            </div>
            {checklist.alcohol_consumed && (
              <div className="pt-2">
                <Chip tone="warn" dot>Alcohol logged</Chip>
              </div>
            )}
          </Card>

          <div className="animate-rise flex flex-col gap-4 lg:col-span-5" style={rise(6)}>
            <Link
              to="/today"
              className="btn-primary block text-center py-3.5 font-mono uppercase tracking-[0.16em] text-sm"
            >
              Log today →
            </Link>

            {range && range.length > 0 && (
              <div className="card p-4 space-y-2">
                <button
                  type="button"
                  onClick={() => setWeeklyOpen((v) => !v)}
                  className="flex items-baseline justify-between gap-2 w-full text-left"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-faint">
                    {weeklyOpen ? '▾ This week' : '▸ This week'}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.16em] text-fg-faint">
                    {range.length} days
                  </span>
                </button>
                {weeklyOpen && <WeeklyMacroChart entries={range.slice(-7)} />}
              </div>
            )}
          </div>
        </div>

        <nav className="animate-rise mt-8 flex items-center justify-center gap-3 pt-1">
          <Link
            to="/review"
            className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg"
          >
            Weekly review
          </Link>
          <span className="text-fg-faint text-[10px]" aria-hidden>·</span>
          <Link
            to="/insurance"
            className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg"
          >
            Insurance
          </Link>
          <span className="text-fg-faint text-[10px]" aria-hidden>·</span>
          <Link
            to="/calendar"
            className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg"
          >
            Calendar
          </Link>
          <span className="text-fg-faint text-[10px]" aria-hidden>·</span>
          <Link
            to="/settings"
            className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg"
          >
            Settings
          </Link>
        </nav>
      </div>
      <Nav />
    </>
  );
}

// isoNDaysBefore returns the YYYY-MM-DD that is `n` days before `iso`.
// Used to compute the start of the trailing weight sparkline + weekly chart.
function isoNDaysBefore(iso: string, n: number): string {
  const base = iso.length > 10 ? iso.slice(0, 10) : iso;
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// rangeToSparklinePoints projects the range payload into the input format
// the WeightSparkline component expects (date + weight pair, with NULLs
// preserved as gaps).
function rangeToSparklinePoints(entries: DailyRangeEntry[]): SparklinePoint[] {
  return entries.map((e) => ({ date: e.date, weight: e.weight_kg }));
}

// binActualsByWeek collapses daily weigh-ins into one point per bet-week,
// using the most recent logged weight in each week. Mirrors the server's
// WeekNumberForBet — week N covers days [N*7-7 .. N*7-1] from bet start.
//
// Both inputs are YYYY-MM-DD; we parse them as UTC midnight so the arithmetic
// is DST-stable. (Local-time parse can be off by 1 day across spring-forward
// transitions — Math.floor on a 23h delta drops you into the prior week.)
//
// Exported so the helper can be unit-tested without rendering Dashboard.
export function binActualsByWeek(
  entries: DailyRangeEntry[],
  betStartingDate: string,
): TrajectoryActual[] {
  const startMs = parseDateUTC(betStartingDate);
  if (startMs === null) return [];
  const byWeek = new Map<number, { date: string; weight_kg: number }>();
  for (const e of entries) {
    if (e.weight_kg === null) continue;
    const dayMs = parseDateUTC(e.date);
    if (dayMs === null) continue;
    const days = Math.floor((dayMs - startMs) / 86_400_000);
    if (days < 0) continue;
    const week = Math.floor(days / 7) + 1;
    const prev = byWeek.get(week);
    // Keep the latest date in the week. On duplicate dates (which the API
    // contract forbids but defensive guards still matter), the first entry
    // in iteration order wins — the strict `>` keeps prev rather than
    // overwriting with the same date.
    if (!prev || e.date > prev.date) {
      byWeek.set(week, { date: e.date, weight_kg: e.weight_kg });
    }
  }
  return [...byWeek.entries()]
    .map(([week_number, v]) => ({ week_number, weight_kg: v.weight_kg }))
    .sort((a, b) => a.week_number - b.week_number);
}

// parseDateUTC parses a YYYY-MM-DD string as UTC midnight in milliseconds,
// or returns null for an unparseable string. Avoids local-time/DST issues.
function parseDateUTC(date: string): number | null {
  // Match exactly YYYY-MM-DD; reject anything fancy upstream of new Date().
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const ms = Date.parse(date + 'T00:00:00Z');
  return Number.isNaN(ms) ? null : ms;
}

// addDaysISO returns the YYYY-MM-DD `n` days after `iso`. Used to display the
// end of the creatine masking window in the dashboard chip.
function addDaysISO(iso: string, n: number): string {
  const base = iso.length > 10 ? iso.slice(0, 10) : iso;
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

// shortMealLabel keeps the dashboard pill compact: "Meal 1 (post-workout)" or
// "Breakfast" both collapse to "Meal 1" / "Breakfast" so a 3-column grid
// doesn't wrap mid-pill on iPhone-sized viewports.
function shortMealLabel(label: string, fallbackID: string): string {
  if (label) {
    const stripped = label.replace(/\s*\([^)]*\)\s*/g, '').trim();
    if (stripped.length > 0) return stripped;
  }
  return fallbackID.charAt(0).toUpperCase() + fallbackID.slice(1);
}

// formatStepsShort emits compact "8.4/14" — fits inside the today-pill row
// without wrapping. Drops the trailing 'k' since the unit is implicit when
// both values are >= 1k.
function formatStepsShort(current: number, target: number): string {
  return `${formatK(current)}/${formatK(target)}`;
}

function formatK(n: number): string {
  if (n >= 1000) {
    const v = n / 1000;
    return v >= 10 ? Math.round(v).toString() : v.toFixed(1);
  }
  return String(n);
}

function fmtDelta(d: number | null): string {
  if (d === null) return '—';
  const sign = d > 0 ? '+' : '';
  return `${sign}${d.toFixed(1)} kg`;
}

function paceTone(s: string): ChipTone {
  switch (s) {
    case 'on_pace':
      return 'good';
    case 'slow':
      return 'warn';
    case 'off_pace':
      return 'danger';
    default:
      return 'muted';
  }
}

function paceText(s: string): string {
  switch (s) {
    case 'on_pace':
      return 'On pace';
    case 'slow':
      return 'Slow';
    case 'off_pace':
      return 'Off pace';
    default:
      return 'No data';
  }
}

// GoalProbabilityCard shows the dashboard's headline number — the
// estimated chance the user hits target by deadline. Driven by the
// server-side estimate (rate-ratio + compliance heuristic). The body
// shows the math: required kg/week vs observed kg/week, when we have
// enough weigh-ins to compute a trailing rate.
//
// Every figure here is nullable on purpose. A `null` means the server has no
// basis for the number, and the card must say so — an invented default here
// reads as reassurance about a bet the user could still lose.
//
// Exported for unit tests: the unknown states are the point of this component
// and they can't be reached through the route without stubbing the API.
export function GoalProbabilityCard({
  pct,
  status,
  trailingLossKg,
  kgToTarget,
  daysToDeadline,
  className,
  style,
}: {
  pct: number | null;
  status: 'on_pace' | 'slow' | 'off_pace' | 'no_data';
  trailingLossKg: number | null;
  kgToTarget: number | null;
  daysToDeadline: number;
  className?: string;
  style?: CSSProperties;
}) {
  // `pct === null` is "not enough data to say". It must never reach `pctTier`
  // (whose lowest bucket is still a verdict) nor `useCountUp` (which takes a
  // number) — so bucket only a real number, and feed the animation a 0 that
  // the unknown branch below never renders.
  const tier = pct !== null ? pctTier(pct) : null;
  const shownPct = useCountUp(pct ?? 0);
  // Already at or below target weight: no further loss required. We suppress
  // the "Need X kg/wk" line so it doesn't confuse with a non-zero target.
  const targetReached = kgToTarget !== null && kgToTarget <= 0;
  const weeksLeft = Math.max(daysToDeadline / 7, 0.1);
  // No weight on file → no requirement to state. Falling back to the block's
  // planned loss rate would present a template default as this user's number.
  const requiredKgPerWeek = kgToTarget !== null && kgToTarget > 0 ? kgToTarget / weeksLeft : null;
  return (
    <Card eyebrow="Hitting target" className={className} style={style}>
      <div className="flex items-end justify-between gap-3">
        <div>
          {tier !== null ? (
            <p
              className={`font-display text-5xl leading-none tabular-nums ${tier.numberClass}`}
            >
              {shownPct}
              <span className="text-2xl text-fg-muted">%</span>
            </p>
          ) : (
            <p className="font-display text-5xl leading-none tabular-nums text-fg-muted">—</p>
          )}
          <p className="label-eyebrow mt-1">{paceText(status)}</p>
        </div>
        <div className="flex flex-col items-end gap-1 pb-1">
          {/* No chip without a number: a confidence label is exactly the
              reassurance we have no grounds for. */}
          {tier !== null && (
            <Chip tone={tier.chipTone} dot>
              {tier.label}
            </Chip>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
            {weeksLeft.toFixed(1)} wk left
          </span>
        </div>
      </div>
      {tier === null && (
        <p className="text-xs text-fg-muted leading-relaxed">
          No estimate yet — this needs weigh-ins to measure a trend against. Log your weight and
          a real number appears here.
        </p>
      )}
      <div className="grid grid-cols-1 gap-1 pt-4 mt-4 border-t border-line">
        {targetReached ? (
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
              Status
            </span>
            <span className="font-mono text-sm tabular-nums text-good">
              target reached · hold to deadline
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                Need
              </span>
              <span
                className={`font-mono text-sm tabular-nums ${
                  requiredKgPerWeek === null ? 'text-fg-muted' : 'text-fg'
                }`}
              >
                {requiredKgPerWeek !== null ? `${requiredKgPerWeek.toFixed(2)} kg/wk` : '—'}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                Tracking
              </span>
              <span
                // A measured rate is real data even with no requirement to
                // judge it against — show it, but stay neutral rather than
                // colouring it good/warn/danger off a number we don't have.
                className={`font-mono text-sm tabular-nums ${
                  trailingLossKg === null
                    ? 'text-fg-muted'
                    : requiredKgPerWeek === null
                      ? 'text-fg'
                      : trailingLossKg >= requiredKgPerWeek * 0.85
                        ? 'text-good'
                        : trailingLossKg >= requiredKgPerWeek * 0.5
                          ? 'text-warn'
                          : 'text-danger'
                }`}
              >
                {trailingLossKg !== null ? `${trailingLossKg.toFixed(2)} kg/wk` : 'no data yet'}
              </span>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// Exported for unit tests — bucketing logic for the goal-probability tier.
// Below 40% splits into Catchable (≥25%, recovery is realistic) and Steep
// (<25%, arithmetic is fighting you). Both keep danger styling — the chip
// describes what you can still do, not relabels you as broken.
//
// Takes a real number only. There is deliberately no bucket for "unknown":
// an absent estimate is not a low one, and the caller renders it as an
// em-dash with no chip rather than asking this function for a verdict.
export function pctTier(pct: number): {
  label: string;
  chipTone: ChipTone;
  numberClass: string;
} {
  if (pct >= 80) return { label: 'Likely', chipTone: 'good', numberClass: 'text-good' };
  if (pct >= 60) return { label: 'On track', chipTone: 'neutral', numberClass: 'text-fg' };
  if (pct >= 40) return { label: 'At risk', chipTone: 'warn', numberClass: 'text-warn' };
  if (pct >= 25) return { label: 'Catchable', chipTone: 'danger', numberClass: 'text-danger' };
  return { label: 'Steep', chipTone: 'danger', numberClass: 'text-danger' };
}

function CheckPill({ label, done }: { label: string; done: boolean }) {
  return (
    <div
      className={`flex items-center justify-center gap-1 text-center text-[11px] font-medium py-2 px-1.5 rounded-full leading-tight border transition-colors ${
        done
          ? 'bg-good-soft text-good border-good/25'
          : 'bg-bg-sunken text-fg-muted border-line'
      }`}
    >
      {done && (
        <svg
          aria-hidden
          viewBox="0 0 12 12"
          className="size-2.5 shrink-0"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 6.5l2.5 2.5 4.5-5" />
        </svg>
      )}
      <span className="min-w-0">{label}</span>
    </div>
  );
}

// shortLabel kept exported for callers (test files reference it). Returns a
// human-readable session-type label for the schedule strip we previously
// rendered on Dashboard. Now only used by tests + Calendar.
export function shortLabel(type: string): string {
  switch (type) {
    case 'lower_squat':
      return 'Squat';
    case 'lower_hinge':
      return 'Hinge';
    case 'upper':
      return 'Upper';
    case 'conditioning':
      return 'Cond.';
    case 'long_walk':
      return 'Walk';
    case 'easy_conditioning':
      return 'Easy';
    case 'rest':
      return 'Rest';
    default:
      if (!type) return 'Session';
      const head = type.split('_')[0];
      return head.charAt(0).toUpperCase() + head.slice(1);
  }
}
